/* Keep hidden homepage tabs out of the initial document. The original theme
 * owns tab styling, product custom elements and cart behavior. */
const panelSelector = '.collection-tab__tab-content';

function enhanceTabs(tabs) {
  if (tabs.dataset.jdTabsReady || !tabs.isConnected) return;
  tabs.dataset.jdTabsReady = 'true';

  let request;
  let activeRequest = 0;
  const originalSetActive = tabs.setActive.bind(tabs);
  const tabButtons = [...tabs.querySelectorAll('.collection-tab__tab-item')];
  const getPanel = (blockId) => [...tabs.querySelectorAll(panelSelector)]
    .find((panel) => panel.dataset.blockId === blockId);

  // A template keeps unused products inert: no image downloads, Swipers or
  // product listeners until the customer actually selects that panel.
  function fetchPanels() {
    if (request) return request;
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', tabs.dataset.sectionId);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);

    request = fetch(url, { signal: controller.signal, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Collection section request failed');
        return response.text();
      })
      .then((html) => {
        const template = document.createElement('template');
        template.innerHTML = html;
        const section = template.content.querySelector('tab-items');
        if (!section || !section.querySelector(`${panelSelector}:not([data-jd-deferred-panel])`)) {
          throw new Error('Collection section is unavailable');
        }
        return section;
      })
      .catch((error) => {
        request = undefined;
        throw error;
      })
      .finally(() => window.clearTimeout(timeout));
    return request;
  }

  function refreshPanel(panel) {
    // Existing product-form, quickview, swatch and motion custom elements
    // upgrade automatically when innerHTML is inserted into the live panel.
    // Only update the selected carousel; never initialize other hidden tabs.
    requestAnimationFrame(() => {
      if (!panel.isConnected || !panel.classList.contains('active')) return;
      panel.querySelectorAll('slide-section, grid-custom').forEach((slider) => {
        const swiper = slider.swiper || slider.globalSlide;
        if (swiper && !swiper.destroyed) swiper.update();
        else if (slider.matches('slide-section') && typeof slider.initSlide === 'function') {
          slider.classList.remove('lazy-loading-swiper-after');
          slider.initSlide();
        }
      });
      panel.querySelectorAll('.bls-image-js img').forEach((img) => {
        const width = Math.ceil(img.getBoundingClientRect().width);
        if (width > 0) img.sizes = `${width}px`;
        const reveal = () => {
          img.classList.remove('bls-loading-image');
          img.closest('motion-element')?.classList.remove('bls-loading-image');
        };
        if (img.complete) reveal();
        else img.addEventListener('load', reveal, { once: true });
      });
      if (panel.querySelector('video.lazy-video-item') && typeof window.initLazyloadItem === 'function') {
        window.initLazyloadItem();
      }
    });
  }

  tabs.setActive = function setActive(blockId) {
    const panel = getPanel(blockId);
    if (!panel) return;
    const version = ++activeRequest;
    const currentPanel = tabs.querySelector(`${panelSelector}.active`);
    const previousHeight = currentPanel?.getBoundingClientRect().height || 0;
    originalSetActive(blockId);

    if (!panel.hasAttribute('data-jd-deferred-panel')) {
      refreshPanel(panel);
      return;
    }

    // Reserve the previous carousel's space while the request is pending.
    if (previousHeight) panel.style.minHeight = `${previousHeight}px`;
    panel.setAttribute('aria-busy', 'true');
    panel.querySelector('[data-jd-tab-loading]').hidden = false;
    panel.querySelector('[data-jd-tab-error]').hidden = true;

    fetchPanels().then((section) => {
      // A slow response must never reactivate or mount a tab the user left.
      if (version !== activeRequest || !tabs.isConnected) return;
      const source = [...section.querySelectorAll(panelSelector)]
        .find((candidate) => candidate.dataset.blockId === blockId);
      if (!source || source.hasAttribute('data-jd-deferred-panel')) {
        request = undefined;
        throw new Error('Collection tab is unavailable');
      }
      panel.innerHTML = source.innerHTML;
      panel.removeAttribute('data-jd-deferred-panel');
      panel.removeAttribute('aria-busy');
      panel.style.removeProperty('min-height');
      refreshPanel(panel);
    }).catch(() => {
      if (version !== activeRequest || !tabs.isConnected) return;
      panel.removeAttribute('aria-busy');
      panel.querySelector('[data-jd-tab-loading]').hidden = true;
      panel.querySelector('[data-jd-tab-error]').hidden = false;
      // The normal collection link remains available. Selecting the tab again
      // retries the request; failures never replace the original first tab.
    });
  };

  tabs.querySelector('.collection-tab__ta')?.setAttribute('role', 'tablist');
  tabButtons.forEach((tab, index) => {
    const panel = getPanel(tab.dataset.blockId);
    if (!panel) return;
    const id = `jd-tab-${tabs.dataset.sectionId}-${index}`;
    tab.id = id;
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-controls', `${id}-panel`);
    panel.id = `${id}-panel`;
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-labelledby', id);
  });

  tabs.addEventListener('click', (event) => {
    const tab = event.target.closest('.collection-tab__tab-item');
    if (!tab || !tabs.contains(tab)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      // Preserve native open-in-new-tab behavior without switching this page.
      event.stopImmediatePropagation();
      return;
    }
    event.preventDefault();
  }, true);

  tabs.addEventListener('keydown', (event) => {
    const tab = event.target.closest('.collection-tab__tab-item');
    if (!tab || !tabButtons.includes(tab)) return;
    const index = tabButtons.indexOf(tab);
    const rtl = getComputedStyle(tabs).direction === 'rtl';
    let next;
    if (event.key === 'ArrowRight') next = index + (rtl ? -1 : 1);
    if (event.key === 'ArrowLeft') next = index + (rtl ? 1 : -1);
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabButtons.length - 1;
    if (event.key === ' ') next = index;
    if (next === undefined) return;
    event.preventDefault();
    const target = tabButtons[(next + tabButtons.length) % tabButtons.length];
    target.focus();
    tabs.setActive(target.dataset.blockId);
  });
}

function initialize(root = document) {
  root.querySelectorAll('tab-items[data-jd-deferred-tabs]').forEach(enhanceTabs);
}

customElements.whenDefined('tab-items').then(() => {
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
});
